import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import config from "../../config";
import { useFilters } from "context/FiltersContext";
import { useCategories } from "context/CategoriesContext";

import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Pagination,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EventNoteIcon from "@mui/icons-material/EventNote";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";

/* ------------------------------------------------------------------ */
/* Helpers                                                              */
/* ------------------------------------------------------------------ */

function formatDate(date) {
  if (!date) return "—";
  return new Date(date).toLocaleDateString("el-GR");
}

function requestStatusColor(status) {
  switch (status) {
    case "open":
      return "success";
    case "closed":
      return "warning";
    case "expired":
    case "cancelled":
      return "error";
    default:
      return "default";
  }
}

function offerStatusColor(status) {
  switch (status) {
    case "new":
    case "sent":
    case "viewed":
      return "info";
    case "kept":
    case "accepted":
      return "success";
    case "rejected":
    case "declined":
      return "error";
    default:
      return "default";
  }
}

/* ------------------------------------------------------------------ */
/* OfferForm — inline form under each request row                      */
/* ------------------------------------------------------------------ */
function OfferForm({ requestId, token, onSuccess, onSubmitted, onAlreadySubmitted, t }) {
  const [price, setPrice] = useState("");
  const [message, setMessage] = useState(t("eventRequests.fieldMessageDefault"));
  const [contactTimes, setContactTimes] = useState(t("eventRequests.contactTimesDefault"));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setResult(null);

    if (!message.trim()) {
      setError(t("eventRequests.fieldMessage") + ": " + t("common.error"));
      return;
    }

    const parsedPrice = price.trim() !== "" ? parseFloat(price) : null;
    if (parsedPrice !== null && (isNaN(parsedPrice) || parsedPrice < 0)) {
      setError(t("eventRequests.fieldPrice") + ": " + t("common.error"));
      return;
    }

    try {
      setSubmitting(true);
      const body = {
        message: message.trim(),
        contactTimes: contactTimes.trim() || t("eventRequests.contactTimesDefault"),
      };
      if (parsedPrice !== null) body.price = parsedPrice;

      const res = await axios.post(
        `${config.BACKEND_BASE_URL}/api/admin/event-requests/${requestId}/offers`,
        body,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setResult(res.data);
      onSubmitted?.(requestId);
    } catch (err) {
      const msg = err?.response?.data?.message || "";
      const isAlreadySubmitted =
        err?.response?.status === 409 ||
        msg.toLowerCase().includes("already submitted");
      if (isAlreadySubmitted) {
        setError(t("eventRequests.alreadySubmittedError"));
        onAlreadySubmitted?.();
      } else {
        setError(msg || t("eventRequests.offerError"));
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (result) {
    return (
      <Box mt={1}>
        <Alert severity="success" sx={{ mb: 1 }}>
          {t("eventRequests.offerSuccess")}
        </Alert>
        {result.userContact && (
          <Box
            sx={{
              bgcolor: "rgba(255,255,255,0.06)",
              borderRadius: 2,
              p: 2,
              mt: 1,
            }}
          >
            <Typography
              variant="subtitle2"
              fontWeight={700}
              sx={{ color: "#fff", mb: 0.5 }}
            >
              {t("eventRequests.userContactTitle")}
            </Typography>
            <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.85)" }}>
              {result.userContact.firstName} {result.userContact.lastName}
            </Typography>
            <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.85)" }}>
              {result.userContact.email}
            </Typography>
            {result.userContact.phone && (
              <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.85)" }}>
                {result.userContact.phone}
              </Typography>
            )}
            <Typography
              variant="caption"
              sx={{ color: "rgba(255,255,255,0.5)", mt: 0.5, display: "block" }}
            >
              {t("eventRequests.userContactNote")}
            </Typography>
          </Box>
        )}
        <Button
          size="small"
          sx={{ mt: 1, color: "rgba(255,255,255,0.6)" }}
          onClick={() => onSuccess && onSuccess(requestId)}
        >
          {t("common.close")}
        </Button>
      </Box>
    );
  }

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      mt={1}
      sx={{
        bgcolor: "rgba(255,255,255,0.04)",
        borderRadius: 2,
        p: 2,
      }}
    >
      <Typography
        variant="subtitle2"
        fontWeight={700}
        sx={{ color: "#fff", mb: 1.5 }}
      >
        {t("eventRequests.offerFormTitle")}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 1.5 }}>
          {error}
        </Alert>
      )}

      <Stack spacing={1.5}>
        <TextField
          label={t("eventRequests.fieldMessage")}
          placeholder={t("eventRequests.fieldMessagePlaceholder")}
          multiline
          rows={3}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          size="small"
          required
          sx={{
            "& .MuiInputBase-input": { color: "#fff" },
            "& .MuiInputLabel-root": { color: "rgba(255,255,255,0.6)" },
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "rgba(255,255,255,0.2)",
            },
          }}
        />
        <TextField
          label={t("eventRequests.fieldPrice")}
          placeholder={t("eventRequests.fieldPricePlaceholder")}
          type="number"
          value={price}
          onChange={(e) => setPrice(e.target.value)}
          size="small"
          inputProps={{ min: 0, step: 1 }}
          helperText={t("eventRequests.fieldPriceHint")}
          FormHelperTextProps={{ sx: { color: "rgba(255,255,255,0.45)" } }}
          sx={{
            "& .MuiInputBase-input": { color: "#fff" },
            "& .MuiInputLabel-root": { color: "rgba(255,255,255,0.6)" },
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "rgba(255,255,255,0.2)",
            },
          }}
        />
        <TextField
          label={t("eventRequests.fieldContactTimes")}
          placeholder={t("eventRequests.contactTimesDefault")}
          value={contactTimes}
          onChange={(e) => setContactTimes(e.target.value)}
          size="small"
          sx={{
            "& .MuiInputBase-input": { color: "#fff" },
            "& .MuiInputLabel-root": { color: "rgba(255,255,255,0.6)" },
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "rgba(255,255,255,0.2)",
            },
          }}
        />
        <Button
          type="submit"
          variant="contained"
          color="primary"
          disabled={submitting}
          size="small"
          sx={{ alignSelf: "flex-start" }}
        >
          {submitting
            ? t("eventRequests.submitting")
            : t("eventRequests.submitOffer")}
        </Button>
      </Stack>
    </Box>
  );
}

/* ------------------------------------------------------------------ */
/* RequestDetailModal                                                   */
/* ------------------------------------------------------------------ */
function RequestDetailModal({ open, onClose, onMakeOffer, alreadySubmitted, row, affiliateCategory, cityLabels, t }) {
  if (!row) return null;

  const matchedCat = row.requestedCategories?.find(
    (c) => c.category === affiliateCategory
  );

  const dateRange = row.eventDate?.from
    ? formatDate(row.eventDate.from) +
      (row.eventDate.to && row.eventDate.to !== row.eventDate.from
        ? ` – ${formatDate(row.eventDate.to)}`
        : "")
    : "—";

  const labelSx = { color: "rgba(255,255,255,0.7)", mb: 0.75 };
  const valueSx = { color: "#fff", fontWeight: 600, fontSize: "1rem", wordBreak: "break-word" };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: "#1a1a2e",
          color: "#fff",
          borderRadius: 3,
          border: "1px solid rgba(255,255,255,0.12)",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pb: 1,
          borderBottom: "1px solid rgba(255,255,255,0.1)",
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <EventNoteIcon sx={{ color: "#fff" }} />
          <Typography fontWeight={900} variant="h6">
            {t("eventRequests.detailTitle")}
          </Typography>
        </Stack>
        <IconButton onClick={onClose} size="small" sx={{ color: "rgba(255,255,255,0.6)" }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2.5 }}>
        <Stack spacing={2.5} mt={2}>

          {/* Name + Town */}
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <Box flex={1}>
              <Typography variant="body2" sx={labelSx}>
                {t("eventRequests.colRequester")}
              </Typography>
              <Typography variant="body1" sx={valueSx}>
                {row.userName || "—"}
              </Typography>
            </Box>
            <Box flex={1}>
              <Typography variant="body2" sx={labelSx}>
                {t("eventRequests.colCity")}
              </Typography>
              <Typography variant="body1" sx={valueSx}>
                {(cityLabels && cityLabels[row.city]) || row.city || "—"}
              </Typography>
            </Box>
          </Stack>

          {/* Dates */}
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <Box flex={1}>
              <Typography variant="body2" sx={labelSx}>
                {t("eventRequests.colEventDate")}
              </Typography>
              <Typography variant="body1" sx={valueSx}>
                {dateRange}
              </Typography>
            </Box>
            {row.offersDeadline && (
              <Box flex={1}>
                <Typography variant="body2" sx={labelSx}>
                  {t("eventRequests.offersUntil")}
                </Typography>
                <Typography variant="body1" sx={valueSx}>
                  {formatDate(row.offersDeadline)}
                </Typography>
              </Box>
            )}
          </Stack>

          <Divider sx={{ borderColor: "rgba(255,255,255,0.1)" }} />

          {/* Max Budget */}
          <Box>
            <Typography variant="body2" sx={labelSx}>
              {t("eventRequests.maxBudget")}
            </Typography>
            <Typography variant="body1" sx={valueSx}>
              {matchedCat?.maxBudget != null ? `€${matchedCat.maxBudget}` : t("eventRequests.noBudget")}
            </Typography>
          </Box>

          {/* Category Message */}
          <Box>
            <Typography variant="body2" sx={labelSx}>
              {t("eventRequests.detailCategoryMessage")}
            </Typography>
            <Box
              sx={{
                borderRadius: 3,
                border: "1px solid rgba(255,255,255,0.16)",
                bgcolor: "rgba(255,255,255,0.04)",
                minHeight: 60,
                p: 2,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: matchedCat?.message ? "#fff" : "rgba(255,255,255,0.4)",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  lineHeight: 1.7,
                  fontSize: "0.95rem",
                }}
              >
                {matchedCat?.message || t("eventRequests.noMessage")}
              </Typography>
            </Box>
          </Box>

          {/* General Notes */}
          <Box>
            <Typography variant="body2" sx={labelSx}>
              {t("eventRequests.requestNotes")}
            </Typography>
            <Box
              sx={{
                borderRadius: 3,
                border: "1px solid rgba(255,255,255,0.16)",
                bgcolor: "rgba(255,255,255,0.04)",
                minHeight: 60,
                p: 2,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: row.notes ? "#fff" : "rgba(255,255,255,0.4)",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  lineHeight: 1.7,
                  fontSize: "0.95rem",
                }}
              >
                {row.notes || t("eventRequests.noNotes")}
              </Typography>
            </Box>
          </Box>

        </Stack>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          pb: 2.5,
          pt: 1.5,
          borderTop: "1px solid rgba(255,255,255,0.08)",
        }}
      >
        <Button
          variant="outlined"
          onClick={onClose}
          sx={{ borderColor: "rgba(255,255,255,0.3)", color: "#fff", flex: 1 }}
        >
          {t("common.close")}
        </Button>
        {alreadySubmitted ? (
          <Typography
            variant="body2"
            sx={{ flex: 1, textAlign: "center", color: "rgba(255,255,255,0.5)", fontStyle: "italic" }}
          >
            {t("eventRequests.alreadySubmittedLabel")}
          </Typography>
        ) : (
          <Button
            variant="contained"
            onClick={onMakeOffer}
            sx={{ flex: 1 }}
          >
            {t("eventRequests.makeOffer")}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

/* ------------------------------------------------------------------ */
/* FeedTab                                                              */
/* ------------------------------------------------------------------ */
function FeedTab({ token, t }) {
  const { i18n } = useTranslation();
  const lang = i18n.language;
  const { cities } = useFilters();
  const { categories } = useCategories();

  const cityLabels = useMemo(
    () => Object.fromEntries((cities || []).map((c) => [c.nameEn, lang === "gr" ? c.nameGr : c.nameEn])),
    [cities, lang]
  );
  const catLabels = useMemo(
    () => Object.fromEntries((categories || []).map((c) => [c.name, lang === "gr" ? c.gr : c.en])),
    [categories, lang]
  );

  const [rows, setRows] = useState([]);
  const [affiliateInfo, setAffiliateInfo] = useState(null);
  const [selectedCity, setSelectedCity] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [submittedIds, setSubmittedIds] = useState(new Set());
  const [detailItem, setDetailItem] = useState(null);
  const [feedPage, setFeedPage] = useState(1);
  const FEED_PAGE_SIZE = 100;

  const fetchFeed = async (city) => {
    setLoading(true);
    setError(null);
    try {
      const params = city ? `?city=${encodeURIComponent(city)}` : "";
      const res = await axios.get(
        `${config.BACKEND_BASE_URL}/api/admin/event-requests/feed${params}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRows(res.data.feed || []);
      if (res.data.affiliateInfo && !affiliateInfo) {
        setAffiliateInfo(res.data.affiliateInfo);
        setSelectedCity(res.data.affiliateInfo.city);
      }
    } catch (err) {
      setError(t("eventRequests.loadError"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const initialize = async () => {
      await Promise.all([
        fetchFeed(""),
        (async () => {
          try {
            const res = await axios.get(
              `${config.BACKEND_BASE_URL}/api/admin/offers/mine`,
              { headers: { Authorization: `Bearer ${token}` } }
            );
            const ids = (res.data.offers || [])
              .map((o) => o.eventRequest?._id)
              .filter(Boolean);
            if (ids.length > 0) setSubmittedIds(new Set(ids));
          } catch {
            // non-critical, ignore
          }
        })(),
      ]);
    };
    initialize();
  }, [token]);

  const handleCityChange = (city) => {
    setSelectedCity(city);
    setExpandedId(null);
    setFeedPage(1);
    fetchFeed(city);
  };

  const sortedFeedRows = useMemo(
    () => [...rows].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)),
    [rows]
  );
  const feedPageCount = Math.ceil(sortedFeedRows.length / FEED_PAGE_SIZE);
  const pagedFeedRows = sortedFeedRows.slice((feedPage - 1) * FEED_PAGE_SIZE, feedPage * FEED_PAGE_SIZE);

  const handleOfferSubmitted = (requestId) => {
    setSubmittedIds((prev) => new Set([...prev, requestId]));
  };

  const handleOfferSuccess = (requestId) => {
    setSubmittedIds((prev) => new Set([...prev, requestId]));
    setExpandedId(null);
  };

  const cityOptions = useMemo(() => {
    if (!affiliateInfo) return [];
    if (affiliateInfo.coversEverywhere) return [];
    const all = [affiliateInfo.city, ...(affiliateInfo.covers || [])];
    return [...new Set(all)].filter(Boolean);
  }, [affiliateInfo]);

  const inputSx = {
    "& .MuiInputBase-input": { color: "#fff" },
    "& .MuiInputLabel-root": { color: "rgba(255,255,255,0.6)" },
    "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.2)" },
    "& .MuiSvgIcon-root": { color: "rgba(255,255,255,0.6)" },
    "& .MuiSelect-select": { color: "#fff" },
  };

  return (
    <Box mt={2}>
      {/* City filter */}
      {(affiliateInfo?.coversEverywhere || cityOptions.length > 1) && (
        <Box mb={2}>
          {affiliateInfo?.coversEverywhere ? (
            <TextField
              label={t("eventRequests.cityFilterLabel")}
              value={selectedCity}
              onChange={(e) => handleCityChange(e.target.value)}
              size="small"
              placeholder={affiliateInfo?.city || ""}
              sx={{ minWidth: 200, ...inputSx }}
            />
          ) : (
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <Select
                value={selectedCity}
                onChange={(e) => handleCityChange(e.target.value)}
                sx={{
                  color: "#fff",
                  bgcolor: "rgba(255,255,255,0.04)",
                  borderRadius: 2,
                  ".MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.2)" },
                  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.35)" },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.45)" },
                  ".MuiSelect-icon": { color: "#fff" },
                  px: 1,
                  py: 0.25,
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      bgcolor: "#23233a",
                      color: "#fff",
                      border: "1px solid rgba(255,255,255,0.1)",
                      "& .MuiMenuItem-root": { fontSize: "0.95rem" },
                      "& .MuiMenuItem-root.Mui-selected": { bgcolor: "rgba(255,255,255,0.08)" },
                      "& .MuiMenuItem-root:hover": { bgcolor: "rgba(255,255,255,0.06)" },
                    },
                  },
                }}
              >
                {cityOptions.map((c) => (
                  <MenuItem key={c} value={c}>{cityLabels[c] || c}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>
      )}

      {loading && (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      )}

      {!loading && error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}

      {!loading && !error && rows.length === 0 && (
        <Box py={6} textAlign="center">
          <Typography variant="body1" sx={{ color: "#fff", opacity: 0.7 }}>
            {t("eventRequests.noRequests")}
          </Typography>
        </Box>
      )}

      {!loading && !error && pagedFeedRows.map((row) => {
        const isExpanded = expandedId === row._id;
        const alreadySubmitted = submittedIds.has(row._id);

        const dateRange = row.eventDate?.from
          ? formatDate(row.eventDate.from) +
            (row.eventDate.to && row.eventDate.to !== row.eventDate.from
              ? ` – ${formatDate(row.eventDate.to)}`
              : "")
          : "—";

        return (
          <Card
            key={row._id}
            sx={{
              mb: 2,
              p: { xs: 1.5, sm: 2 },
              bgcolor: "rgba(255,255,255,0.03)",
              borderRadius: 3,
              border: "1px solid rgba(255,255,255,0.1)",
            }}
          >
            <Stack direction={{ xs: "column", md: "row" }} alignItems={{ xs: "stretch", md: "center" }} spacing={2}>
              {/* Left: content */}
              <Box flex={1} minWidth={0}>
                {/* Status + Title */}
                <Box mb={0.75}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: row.status === "open" ? "#81c784" : "#ffa726",
                      fontStyle: "italic",
                      display: "block",
                    }}
                  >
                    {row.status === "open"
                      ? t("eventRequests.reqLabelOpen")
                      : t("eventRequests.reqLabelClosed")}
                  </Typography>
                  <Typography
                    variant="subtitle1"
                    fontWeight={700}
                    sx={{ color: "#fff", minWidth: 0, wordBreak: "break-word" }}
                  >
                    {row.title || row.occasion?.nameGr || row.occasion?.nameEn || "—"}
                  </Typography>
                </Box>

                {/* Row 1: userName + seeking chip + city */}
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" mb={0.5} useFlexGap>
                  {row.userName && (
                    <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.75)" }}>
                      {row.userName}
                    </Typography>
                  )}
                  {affiliateInfo?.category && (
                    <Chip
                      label={`${t("eventRequests.seekingFor")} ${catLabels[affiliateInfo.category] || affiliateInfo.category}`}
                      size="small"
                      sx={{ color: "#fff", bgcolor: "rgba(255,255,255,0.08)" }}
                    />
                  )}
                  <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.75)" }}>
                    {cityLabels[row.city] || row.city}
                  </Typography>
                </Stack>

                {/* Row 2: dates */}
                <Stack direction="row" spacing={1.5} flexWrap="wrap" alignItems="center" useFlexGap>
                  <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.6)" }}>
                    {dateRange}
                  </Typography>
                  {row.offersDeadline && (
                    <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.6)" }}>
                      {t("eventRequests.offersUntil")}: {formatDate(row.offersDeadline)}
                    </Typography>
                  )}
                </Stack>
              </Box>

              {/* Right: buttons — vertically centered on md+, stacked on mobile */}
              <Stack
                direction={{ xs: "column", sm: "row", md: "column" }}
                spacing={1}
                flexShrink={0}
              >
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setDetailItem(row)}
                  sx={{ color: "#fff", borderColor: "rgba(255,255,255,0.3)", whiteSpace: "nowrap" }}
                >
                  {t("eventRequests.viewDetails")}
                </Button>
                {alreadySubmitted ? (
                  <Typography
                    variant="body2"
                    sx={{
                      color: "rgba(255,255,255,0.45)",
                      whiteSpace: "nowrap",
                      textAlign: "center",
                      px: 1,
                    }}
                  >
                    {t("eventRequests.alreadySubmittedLabel")}
                  </Typography>
                ) : (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setExpandedId(isExpanded ? null : row._id)}
                    sx={{ color: "#fff", borderColor: "rgba(255,255,255,0.3)", whiteSpace: "nowrap" }}
                  >
                    {isExpanded ? t("common.cancel") : t("eventRequests.makeOffer")}
                  </Button>
                )}
              </Stack>
            </Stack>

            <Collapse in={isExpanded} unmountOnExit>
              <Divider sx={{ borderColor: "rgba(255,255,255,0.1)", my: 1.5 }} />
              <OfferForm
                requestId={row._id}
                token={token}
                onSubmitted={handleOfferSubmitted}
                onSuccess={handleOfferSuccess}
                onAlreadySubmitted={() => handleOfferSuccess(row._id)}
                t={t}
              />
            </Collapse>
          </Card>
        );
      })}

      {feedPageCount > 1 && (
        <Box display="flex" justifyContent="center" mt={2}>
          <Pagination
            count={feedPageCount}
            page={feedPage}
            onChange={(_, p) => { setFeedPage(p); window.scrollTo({ top: 0, behavior: "smooth" }); }}
            sx={{ "& .MuiPaginationItem-root": { color: "#fff" } }}
          />
        </Box>
      )}

      <RequestDetailModal
        open={detailItem !== null}
        onClose={() => setDetailItem(null)}
        onMakeOffer={() => {
          const id = detailItem?._id;
          setDetailItem(null);
          setExpandedId(id);
        }}
        alreadySubmitted={submittedIds.has(detailItem?._id)}
        row={detailItem}
        affiliateCategory={affiliateInfo?.category}
        cityLabels={cityLabels}
        t={t}
      />
    </Box>
  );
}

/* ------------------------------------------------------------------ */
/* MyOffersTab                                                          */
/* ------------------------------------------------------------------ */
const OFFERS_PAGE_SIZE = 100;

function MyOffersTab({ token, t }) {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [offersPage, setOffersPage] = useState(1);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(
          `${config.BACKEND_BASE_URL}/api/admin/offers/mine`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setOffers(res.data.offers || []);
      } catch (err) {
        setError(t("eventRequests.loadError"));
      } finally {
        setLoading(false);
      }
    })();
  }, [token, t]);

  const sortedOffers = useMemo(
    () => [...offers].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)),
    [offers]
  );
  const offersPageCount = Math.ceil(sortedOffers.length / OFFERS_PAGE_SIZE);
  const pagedOffers = sortedOffers.slice((offersPage - 1) * OFFERS_PAGE_SIZE, offersPage * OFFERS_PAGE_SIZE);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }

  if (offers.length === 0) {
    return (
      <Box py={6} textAlign="center">
        <Typography variant="body1" sx={{ color: "#fff", opacity: 0.7 }}>
          {t("eventRequests.noOffers")}
        </Typography>
      </Box>
    );
  }

  return (
    <Box mt={2}>
      {pagedOffers.map((offer) => {
        const req = offer.eventRequest;
        const eventDateRange = req?.eventDate?.from
          ? req.eventDate.to && req.eventDate.to !== req.eventDate.from
            ? `${formatDate(req.eventDate.from)} – ${formatDate(req.eventDate.to)}`
            : formatDate(req.eventDate.from)
          : "—";
        return (
          <Card
            key={offer._id}
            sx={{
              mb: 2,
              p: 2,
              bgcolor: "rgba(255,255,255,0.03)",
              borderRadius: 3,
              border: "1px solid rgba(255,255,255,0.1)",
            }}
          >
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={2}
              alignItems={{ xs: "flex-start", md: "center" }}
              justifyContent="space-between"
            >
              <Box flex={1}>
                <Box mb={0.5}>
                  <Typography
                    variant="caption"
                    sx={{
                      color:
                        ["kept", "accepted"].includes(offer.status)
                          ? "#81c784"
                          : offer.status === "rejected"
                          ? "#e57373"
                          : offer.status === "viewed"
                          ? "#64b5f6"
                          : "rgba(255,255,255,0.55)",
                      fontStyle: "italic",
                      display: "block",
                    }}
                  >
                    {["new", "sent"].includes(offer.status)
                      ? t("eventRequests.offerLabelNew")
                      : offer.status === "viewed"
                      ? t("eventRequests.offerLabelViewed")
                      : ["kept", "accepted"].includes(offer.status)
                      ? t("eventRequests.offerLabelKept")
                      : t("eventRequests.offerLabelRejected")}
                  </Typography>
                  <Typography
                    variant="subtitle1"
                    fontWeight={700}
                    sx={{ color: "#fff" }}
                  >
                    {req?.title || req?.occasion?.nameGr || req?.occasion?.nameEn || "—"}
                  </Typography>
                </Box>

                <Stack direction="row" spacing={2} flexWrap="wrap">
                  <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.75)" }}>
                    <strong style={{ color: "#fff" }}>
                      {t("eventRequests.colCity")}:
                    </strong>{" "}
                    {req?.city || "—"}
                  </Typography>
                  {req?.userName && (
                    <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.75)" }}>
                      <strong style={{ color: "#fff" }}>
                        {t("eventRequests.colRequester")}:
                      </strong>{" "}
                      {req.userName}
                    </Typography>
                  )}
                  <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.75)" }}>
                    <strong style={{ color: "#fff" }}>
                      {t("eventRequests.colEventDate")}:
                    </strong>{" "}
                    {eventDateRange}
                  </Typography>
                  {offer.price != null && (
                    <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.75)" }}>
                      <strong style={{ color: "#fff" }}>
                        {t("eventRequests.colPrice")}:
                      </strong>{" "}
                      €{offer.price}
                    </Typography>
                  )}
                  <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.75)" }}>
                    <strong style={{ color: "#fff" }}>
                      {t("eventRequests.colCategory")}:
                    </strong>{" "}
                    {offer.category}
                  </Typography>
                </Stack>

                {offer.message && (
                  <Typography
                    variant="body2"
                    sx={{ color: "rgba(255,255,255,0.6)", mt: 0.5 }}
                  >
                    {offer.message}
                  </Typography>
                )}

                {offer.contactTimes && (
                  <Typography
                    variant="body2"
                    sx={{ color: "rgba(255,255,255,0.6)", mt: 0.25 }}
                  >
                    <strong style={{ color: "rgba(255,255,255,0.8)" }}>
                      {t("eventRequests.fieldContactTimes")}:
                    </strong>{" "}
                    {offer.contactTimes}
                  </Typography>
                )}

                {req?.status && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: req.status === "open" ? "#81c784" : "#ffa726",
                      fontStyle: "italic",
                      display: "block",
                      mt: 0.25,
                    }}
                  >
                    {req.status === "open"
                      ? t("eventRequests.reqLabelOpen")
                      : t("eventRequests.reqLabelClosed")}
                  </Typography>
                )}
              </Box>

              <Stack spacing={1} alignItems={{ md: "flex-end" }} flexShrink={0}>
                <Typography
                  variant="caption"
                  sx={{ color: "rgba(255,255,255,0.4)", whiteSpace: "nowrap" }}
                >
                  {formatDate(offer.createdAt)}
                </Typography>
              </Stack>
            </Stack>
          </Card>
        );
      })}
      {offersPageCount > 1 && (
        <Box display="flex" justifyContent="center" mt={2}>
          <Pagination
            count={offersPageCount}
            page={offersPage}
            onChange={(_, p) => { setOffersPage(p); window.scrollTo({ top: 0, behavior: "smooth" }); }}
            sx={{ "& .MuiPaginationItem-root": { color: "#fff" } }}
          />
        </Box>
      )}
    </Box>
  );
}

/* ------------------------------------------------------------------ */
/* Main page                                                            */
/* ------------------------------------------------------------------ */
export default function EventRequestsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const token = useMemo(() => localStorage.getItem("admin_jwt"), []);

  return (
    <DashboardLayout>
      <Box py={3} sx={{ color: "#fff" }}>
        <Card sx={{ p: 3, color: "#fff", borderRadius: 3 }}>
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between" flexWrap="wrap" gap={1} mb={2}>
            <Box>
              <Typography variant="h4" fontWeight={700} sx={{ color: "#fff", mb: 0.5 }}>
                {t("eventRequests.feedTitle")}
              </Typography>
              <Typography variant="body2" sx={{ color: "#fff", opacity: 0.7 }}>
                {t("eventRequests.feedSubtitle")}
              </Typography>
            </Box>
            <Button
              variant="outlined"
              size="small"
              onClick={() => navigate("/pages/my-offers")}
              sx={{ color: "#fff", borderColor: "rgba(255,255,255,0.35)", whiteSpace: "nowrap", flexShrink: 0 }}
            >
              {t("eventRequests.myOffersPageBtn")}
            </Button>
          </Stack>

          <FeedTab token={token} t={t} />
        </Card>
      </Box>
    </DashboardLayout>
  );
}
